/**
 * Модель для работы с уведомлениями
 */

import { createModel } from '@rematch/core';
import { RootModel } from './index';
import { v4 as uuidv4 } from 'uuid';

import NotificationsService, { IConfirmNewBody } from '../services/NotificationsService';
import { IAlert, IId, NotificationSeverity } from './types';
import { convertOperatorMessageToAlert } from '../containers/Alerts/utils';

interface IState {
  list: IAlert[];
  confirmations: IAlert[];
  soundEnabled: boolean;
  // Signal that a notifications-hub event arrived, for any mounted consumer that
  // wants to refresh. The hub owner (alerts) emits it; consumers read it — no
  // cross-model dispatch from the hub owner into another model.
  notificationsReloadTick: number;
  notificationsReloadSubscribed: boolean;
}

const initialState: IState = {
  list: [],
  confirmations: [],
  soundEnabled: false,
  notificationsReloadTick: 0,
  notificationsReloadSubscribed: false
};

export const alerts = createModel<RootModel>()({
  state: initialState as IState,
  reducers: {
    setAlerts(state: IState, payload: IAlert[]): IState {
      return {
        ...state,
        list: payload
      };
    },
    setSoundEnabled(state: IState, payload: boolean): IState {
      return {
        ...state,
        soundEnabled: payload
      };
    },
    setConfirmations(state: IState, payload: IAlert[]): IState {
      return {
        ...state,
        confirmations: payload
      };
    },
    bumpNotificationsReload(state: IState): IState {
      return {
        ...state,
        notificationsReloadTick: state.notificationsReloadTick + 1
      };
    },
    setNotificationsReloadSubscribed(state: IState, payload: boolean): IState {
      // Unsubscribing also zeroes the tick so the next subscriber starts from 0
      // — the page's mount-run guard keys off a zero tick, which prevents a
      // stale persisted tick from triggering a spurious reload on remount.
      return payload
        ? { ...state, notificationsReloadSubscribed: true }
        : {
            ...state,
            notificationsReloadSubscribed: false,
            notificationsReloadTick: 0
          };
    }
  },
  effects: (dispatch) => {
    const d = dispatch as any;
    return {
      async getAlerts() {
        const { list } = initialState;

        const { data } = await NotificationsService.getUnreadForOperator({
          Limit: 100,
          MessageSeverity: 'Critical'
        });

        const notifications = (data as any[]).map(convertOperatorMessageToAlert);

        const payload = [...list, ...notifications];

        this.setAlerts(payload);
      },
      async postponeAlert(payload: { id: IId; seconds: number }) {
        try {
          await NotificationsService.postpone(payload.id, payload.seconds);
        } catch (error) {
          return error;
        }
      },
      async confirmAlert(body: IConfirmNewBody) {
        try {
          await NotificationsService.confirm(body);
        } catch (error) {
          return error;
        }
      },
      addAlert(payload: IAlert, rootState: any) {
        const { list } = rootState.alerts;

        if (!payload.uid) {
          payload.uid = uuidv4();
        }
        if (
          payload.trackInfo &&
          payload.userId &&
          payload.severity === NotificationSeverity.Critical
        ) {
          d.infrastructureUsers.alertUser({ userId: payload.userId, trackInfo: payload.trackInfo });
        }

        const existingIndex = list.findIndex((item: IAlert) => item.uid === payload.uid);
        let data: IAlert[];
        if (existingIndex !== -1) {
          const updated = { ...list[existingIndex], ...payload };
          data = [updated, ...list.filter((_item: IAlert, idx: number) => idx !== existingIndex)];
        } else {
          data = [payload, ...list];
        }

        this.setAlerts(data);
      },
      deleteAlert(id: IId, rootState: any) {
        const { list } = rootState.alerts;

        const data = list.filter((item: IAlert) => item.uid !== id);

        this.setAlerts(data);
      },
      addConfirmation(payload: IAlert, rootState: any) {
        const { confirmations } = rootState.alerts;

        const data = [...confirmations, payload];

        this.setConfirmations(data);
      },
      checkConfirmation(id: IId, rootState: any) {
        const { confirmations } = rootState.alerts;

        const data = confirmations.filter((item: IAlert) => item.uid !== id);

        this.setConfirmations(data);
      }
    };
  }
});
